import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  JsonapiEntity,
  PagedTopLevelData,
  ResourceIdentifier,
  TopLevel,
  TopLevelData,
  TopLevelDatum,
} from 'jsonapi-transformers';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
  HttpHeadersWrapper,
  HttpParamsWrapper,
  TarlV3Api,
} from '../tarl-v3-api';

import {
  JsonapiPageQueryParams,
  JsonapiQueryParams,
  PageOf,
  convertEntityToJsonapiBody,
  convertPageQueryParamsToHttpParams,
  convertToJsonApiPagedResponseData,
  convertToJsonapiResponseData,
  convertToJsonapiResponseDatum,
  getDeserialisedDatumOrOriginalEntity,
} from './jsonapi-utils';
import { EMPTY_META, MetaData } from './metadata';

const JSONAPI_HEADERS = {
  Accept: 'application/vnd.api+json',
  'Content-Type': 'application/vnd.api+json',
};

const COMPOUND_DELIMITER = '___';

type QueryParams = { [key: string]: string };

/**
 * Wrap `HttpClient` to provide shared V3 API and JSON:API characteristics.
 *
 * - URLs are prefixed with the AppConfig's API root (domain, version, tenant)
 * - Default headers, including authorization, are added to the request options
 * - Responses are deserialised.
 */
@Injectable()
export class TarlV3Jsonapi {
  constructor(private httpClient: HttpClient, protected tarlV3Api: TarlV3Api) {}

  prefixUrl(url: string) {
    return this.tarlV3Api.prefixUrl(url);
  }

  /**
   * Makes an HTTP GET request to a TARL v3 JSON:API endpoint, expecting a
   * single resource to be returned, deserialised.
   *
   * @param {string} url - A route path, relative to the V3 API base route.
   * @param {JsonapiQueryParams} params - Optional JSON:API query params
   * @param {QueryParams} additionalParams - Optional additional query params
   * @param {boolean} isDraft - True to return the draft list; false for published list
   * @returns {Observable<T>} - A response, with JSON:API deserialisation
   */
  getResource<T extends JsonapiEntity<T>>(
    url: string,
    params: JsonapiQueryParams = {},
    additionalParams: QueryParams = {},
  ): Observable<T> {
    const buildRequest = () =>
      this.httpClient.get<TopLevelDatum>(this.prefixUrl(url), {
        ...this.getCommonHeaders(),
        ...this.getQueryParams(params, additionalParams),
      });
    return buildRequest().pipe(
      catchError(this.tarlV3Api.handleAuthenticationFailure(buildRequest)),
      map(topLevelDatum => convertToJsonapiResponseDatum<T>(topLevelDatum)),
    );
  }

  /**
   * Makes an HTTP GET request to a TARL v3 JSON:API endpoint, expecting an
   * array of resources to be returned, deserialised.
   *
   * @param {string} url - A route path, relative to the V3 API base route.
   * @param {JsonapiQueryParams} params - Optional JSON:API query params
   * @param {QueryParams} additionalParams - Optional additional query params
   * @returns {Observable<T[]>} - A response, with JSON:API deserialisation
   */
  getResources<T extends JsonapiEntity<T>>(
    url: string,
    params: JsonapiQueryParams = {},
    additionalParams: QueryParams = {},
  ): Observable<T[]> {
    const buildRequest = () =>
      this.httpClient.get<TopLevelData>(this.prefixUrl(url), {
        ...this.getCommonHeaders(),
        ...this.getQueryParams(params, additionalParams),
      });
    return buildRequest().pipe(
      catchError(this.tarlV3Api.handleAuthenticationFailure(buildRequest)),
      map(topLevelData => convertToJsonapiResponseData<T>(topLevelData)),
    );
  }

  /**
   * Makes an HTTP GET request to a TARL v3 JSON:API endpoint, expecting a
   * page of resources to be returned, deserialised.
   *
   * @param {string} url - A route path, relative to the V3 API base route.
   * @param {JsonapiPageQueryParams} params - Optional JSON:API page query params
   * @returns {Observable<PageOf<T>>} - A response, with JSON:API deserialisation
   */
  getPageOfResources<T extends JsonapiEntity<T>>(
    url: string,
    params: JsonapiPageQueryParams = {},
    additionalParams: QueryParams = {},
  ): Observable<PageOf<T>> {
    const buildRequest = () =>
      this.httpClient.get<PagedTopLevelData>(this.prefixUrl(url), {
        ...this.getCommonHeaders(),
        ...this.getQueryParams(params, additionalParams),
      });
    return buildRequest().pipe(
      catchError(this.tarlV3Api.handleAuthenticationFailure(buildRequest)),
      map(pagedTopLevelData =>
        convertToJsonApiPagedResponseData<T>(pagedTopLevelData),
      ),
    );
  }

  /**
   * Makes an HTTP GET request to a TARL v3 JSON:API endpoint, returning
   * the raw JSON:API response. Used to GET JSON:API relationships.
   *
   * @param {string} url - A route path, relative to the V3 API base route.
   * @param {JsonapiQueryParams} params - Optional JSON:API query params
   * @returns {Observable<T>} - A raw JSON:API response object
   */
  getRawJsonapiResponse<T extends TopLevel>(
    url: string,
    params: JsonapiQueryParams = {},
  ): Observable<T> {
    const buildRequest = () =>
      this.httpClient.get<T>(this.prefixUrl(url), {
        ...this.getCommonHeaders(),
        ...this.getQueryParams(params),
      });
    return buildRequest().pipe(
      catchError(this.tarlV3Api.handleAuthenticationFailure(buildRequest)),
    );
  }

  /**
   * Makes an HTTP HEAD request to a TARL v3 JSON:API endpoint, expecting a successful
   * 200 response with an empty body
   *
   * @param {string} url - A route path, relative to the V3 API base route.
   * @returns {Observable<HttpResponse<null>>} - A response, with an empty body
   */
  head(url: string): Observable<HttpResponse<null>> {
    const buildRequest = () =>
      this.httpClient.head<null>(this.prefixUrl(url), {
        observe: 'response',
        ...this.getCommonHeaders(),
      });
    return buildRequest().pipe(
      catchError(this.tarlV3Api.handleAuthenticationFailure(buildRequest)),
    );
  }

  /**
   * Makes an HTTP PATCH request to a TARL v3 JSON:API endpoint, expecting a
   * single resource to be returned, deserialised, or a 204 (return provided entity).
   *
   * @template T - a subtype of JsonapiEntity, covering the data being PATCHed
   * @param {string} url - A route path, relative to the V3 API base route.
   * @param {T} entity - The entity to include in the request body.
   * @param {JsonapiQueryParams} params - Optional JSON:API query params
   * @param {QueryParams} additionalParams - Optional additional query params
   * @returns {Observable<T>} - A response, with JSON:API deserialisation
   */
  patchResource<T extends JsonapiEntity<T>>(
    url: string,
    entity: T,
    params: JsonapiQueryParams = {},
    additionalParams: QueryParams = {},
  ): Observable<T> {
    const buildRequest = () =>
      this.httpClient.patch<TopLevelDatum | null>(
        this.prefixUrl(url),
        convertEntityToJsonapiBody(entity),
        {
          ...this.getCommonHeaders(),
          ...this.getQueryParams(params, additionalParams),
        },
      );
    return buildRequest().pipe(
      catchError(this.tarlV3Api.handleAuthenticationFailure(buildRequest)),
      map(getDeserialisedDatumOrOriginalEntity<T>(entity)),
    );
  }

  /**
   * Makes an HTTP PATCH request to a TARL v3 JSON:API endpoint
   *
   * @param {string} url - A route path, relative to the V3 API base route.
   * @param {T[]} identifiers - The resource identifiers to include in the request body.
   * @param {JsonapiQueryParams} params - Optional JSON:API query params
   * @returns {Observable<MetaData<T>>} - Metadata from the response
   */
  patchToManyRelationship<T extends ResourceIdentifier>(
    url: string,
    identifiers: T[],
    params: JsonapiQueryParams = {},
  ): Observable<MetaData<T[]>> {
    const buildRequest = () =>
      this.httpClient
        .patch<MetaData<T[]>>(
          this.prefixUrl(url),
          { data: identifiers },
          {
            ...this.getCommonHeaders(),
            ...this.getQueryParams(params),
            observe: 'response',
          },
        )
        .pipe(
          map(httpResponse => {
            if (httpResponse instanceof HttpResponse && httpResponse.ok) {
              return httpResponse.status === 204
                ? EMPTY_META
                : httpResponse.body;
            }
            throw httpResponse;
          }),
        );
    return buildRequest().pipe(
      catchError(this.tarlV3Api.handleAuthenticationFailure(buildRequest)),
    );
  }

  /**
   * Makes an HTTP PATCH request to a TARL v3 JSON:API endpoint, returning
   * the raw JSON:API response. Used to PATCH JSON:API relationships.
   *
   * @param {string} url - A route path, relative to the V3 API base route.
   * @param {T} body - Request body
   * @returns {Observable<T>} - A raw JSON:API response object
   */
  patchRawJsonapiResponse<T extends TopLevel>(
    url: string,
    body: T,
  ): Observable<T> {
    const buildRequest = () =>
      this.httpClient.patch<T>(
        this.prefixUrl(url),
        body,
        this.getCommonHeaders(),
      );
    return buildRequest().pipe(
      catchError(this.tarlV3Api.handleAuthenticationFailure(buildRequest)),
    );
  }

  /**
   * Makes an HTTP POST request to a TARL v3 JSON:API endpoint, expecting a
   * single resource to be returned, deserialised, or a 204 (return provided entity).
   *
   * @param {string} url - A route path, relative to the V3 API base route.
   * @param {T} entity - The entity to include in the request body.
   * @returns {Observable<T>} - A response, with JSON:API deserialisation
   */
  postResource<T extends JsonapiEntity<T>>(
    url: string,
    entity: T,
    headers = this.getCommonHeaders(),
    params: QueryParams = {},
  ): Observable<T> {
    const options = {
      headers: headers.headers,
      params: new HttpParams({ fromObject: params }),
    };
    const buildRequest = () =>
      this.httpClient.post<TopLevelDatum | null>(
        this.prefixUrl(url),
        convertEntityToJsonapiBody(entity),
        options,
      );
    return buildRequest().pipe(
      catchError(this.tarlV3Api.handleAuthenticationFailure(buildRequest)),
      map(getDeserialisedDatumOrOriginalEntity<T>(entity)),
    );
  }

  /**
   * Makes an HTTP POST request to a TARL v3 JSON:API relationships endpoint, expecting a
   * a 204 response with an empty body if successful
   *
   * @param {string} url - A route path, relative to the V3 API base route.
   * @param {T[]} identifiers - The resource identifiers to include in the request body.
   * @returns {Observable<HttpResponse<null>>} - A response, with an empty body
   */
  postToManyRelationship<T extends ResourceIdentifier>(
    url: string,
    identifiers: T[],
  ): Observable<HttpResponse<null>> {
    const buildRequest = () =>
      this.httpClient.post<null>(
        this.prefixUrl(url),
        { data: identifiers },
        {
          ...this.getCommonHeaders(),
          observe: 'response',
        },
      );
    return buildRequest().pipe(
      catchError(this.tarlV3Api.handleAuthenticationFailure(buildRequest)),
    );
  }

  /**
   * Makes an HTTP POST request to a TARL v3 JSON:API endpoint, returning
   * the raw JSON:API response. Used to POST JSON:API relationships.
   *
   * @param {string} url - A route path, relative to the V3 API base route.
   * @param {T} body - Request body
   * @returns {Observable<T>} - A raw JSON:API response object
   */
  postRawJsonapiResponse<T extends TopLevel>(
    url: string,
    body: T,
  ): Observable<T> {
    const buildRequest = () =>
      this.httpClient.post<T>(
        this.prefixUrl(url),
        body,
        this.getCommonHeaders(),
      );
    return buildRequest().pipe(
      catchError(this.tarlV3Api.handleAuthenticationFailure(buildRequest)),
    );
  }

  /**
   * Makes an HTTP PUT request to a TARL v3 JSON:API endpoint, expecting a
   * a 204 response with an empty body if successful
   *
   * @param {string} url - A route path, relative to the V3 API base route.
   * @returns {Observable<HttpResponse<null>>} - A response, with an empty body
   */
  put(url: string): Observable<HttpResponse<null>> {
    const buildRequest = () =>
      this.httpClient.put<null>(
        this.prefixUrl(url),
        {},
        {
          ...this.getCommonHeaders(),
          observe: 'response',
        },
      );
    return buildRequest().pipe(
      catchError(this.tarlV3Api.handleAuthenticationFailure(buildRequest)),
    );
  }

  /**
   * Makes an HTTP DELETE request to a TARL v3 JSON:API endpoint, expecting a successful
   * 204 response, with an empty body.
   *
   * @param {string} url - A route path, relative to the V3 API base route.
   * @returns {Observable<HttpResponse<null>>} - A response, with an empty body
   */
  delete(url: string): Observable<HttpResponse<null>> {
    const buildRequest = () =>
      this.httpClient.delete<null>(this.prefixUrl(url), {
        observe: 'response',
        ...this.getCommonHeaders(),
      });
    return buildRequest().pipe(
      catchError(this.tarlV3Api.handleAuthenticationFailure(buildRequest)),
    );
  }

  /**
   * Makes an HTTP DELETE request to a TARL v3 JSON:API endpoint, returning
   * the raw JSON:API meta response.
   *
   * @param {string} url - A route path, relative to the V3 API base route.
   * @param {T} body - Request body
   * @returns {Observable<T>} - A raw JSON:API response object
   */
  deleteRawJsonapiResponse<T extends MetaData<any>>(
    url: string,
    body: T,
  ): Observable<T> {
    const buildRequest = () =>
      this.httpClient.request<T>('DELETE', this.prefixUrl(url), {
        body,
        ...this.getCommonHeaders(),
      });
    return buildRequest().pipe(
      catchError(this.tarlV3Api.handleAuthenticationFailure(buildRequest)),
    );
  }

  /**
   * Construct a URL path using variable replacement.
   *
   * This follows the scheme of URI.expand, which replaces handlebars {}-enclosed variable
   * names with the values provided in a map of replacements.
   *
   * @param {string} pathTemplate - A path, including {}-enclosed variable names
   * @param {object} substitutions - Map of variable-name replacement values
   * @returns {string} - An absolute URL to a V3 API endpoint
   */
  path(
    pathTemplate: string,
    substitutions: { [id: string]: string | number | boolean },
  ): string {
    return this.tarlV3Api.path(pathTemplate, substitutions);
  }

  getQueryParams(
    jsonapiParams: JsonapiQueryParams | JsonapiPageQueryParams,
    additionalParams: QueryParams = {},
  ): HttpParamsWrapper {
    let params = new HttpParams({ fromObject: additionalParams });
    params = convertPageQueryParamsToHttpParams(jsonapiParams, params);
    return { params };
  }

  /**
   * Gets common headers for V3 JSON API requests.
   *
   * @returns {HttpHeadersWrapper} - common headers
   */
  getCommonHeaders(): HttpHeadersWrapper {
    const headers = this.tarlV3Api
      .getAllHeaders()
      .append('Accept', JSONAPI_HEADERS.Accept)
      .append('Content-Type', JSONAPI_HEADERS['Content-Type']);
    return { headers };
  }

  /**
   * Given ordered parts of a compound ID, assemble them into an ID string.
   *
   * @param {string[]} ...parts - an ordered (vararg) list of string ID components
   * @returns {string} - The composed compound ID.
   **/
  compoundId(...parts: string[]): string {
    return parts.join(COMPOUND_DELIMITER);
  }
}
